"use client";

import { createContext, useCallback, useContext, useLayoutEffect, useState } from "react";
import { clearColorCache } from "./colors";

const preventDarkMode = false;

type ColorMode = "system" | "light" | "dark";
type ResolvedTheme = "light" | "dark";

interface ColorModeContextType {
  mode: ColorMode;
  colorMode: ResolvedTheme;
  isDarkTheme: boolean;
  setMode: (mode: ColorMode) => void;
  toggleTheme: () => void;
}

const ColorModeContext = createContext<ColorModeContextType>({
  mode: "system",
  colorMode: "light",
  isDarkTheme: false,
  setMode: () => {
    /* Default implementation */
  },
  toggleTheme: () => {
    /* Default implementation */
  },
});

export const useColorMode = () => useContext(ColorModeContext);

interface ColorModeProviderProps {
  children: React.ReactNode;
  fallbackMode?: ColorMode;
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function getSavedMode(fallbackMode: ColorMode): ColorMode {
  if (typeof window === "undefined") return fallbackMode;
  try {
    const saved = window.localStorage.getItem("colorModePreference");
    if (saved === "light" || saved === "dark" || saved === "system") {
      return saved;
    }
  } catch {
    // Storage may be disabled; keep the selected mode available in memory.
  }
  return fallbackMode;
}

function persistMode(mode: ColorMode): void {
  try {
    window.localStorage.setItem("colorModePreference", mode);
  } catch {
    // A rejected write must not block an in-session mode change.
  }
}

function resolveTheme(mode: ColorMode): ResolvedTheme {
  if (mode === "system") {
    return getSystemTheme();
  }
  return mode;
}

export const ColorModeProvider: React.FC<ColorModeProviderProps> = ({ children, fallbackMode = "system" }) => {
  const [mode, setPreferredMode] = useState<ColorMode>(() => getSavedMode(fallbackMode));
  const [colorMode, setColorMode] = useState<ResolvedTheme>(() => resolveTheme(mode));
  const isDarkTheme = colorMode === "dark";

  const applyTheme = useCallback((theme: ResolvedTheme) => {
    if (theme === "dark") {
      document.documentElement.classList.add("theme-dark");
      document.body.classList.add("theme-dark");
    } else {
      document.documentElement.classList.remove("theme-dark");
      document.body.classList.remove("theme-dark");
    }
    clearColorCache();
  }, []);

  useLayoutEffect(() => {
    clearColorCache();

    const theme = resolveTheme(mode);
    setColorMode(theme);
    applyTheme(theme);

    if (mode === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

      const handleSystemThemeChange = (e: MediaQueryListEvent) => {
        const newTheme = e.matches ? "dark" : "light";
        setColorMode(newTheme);
        applyTheme(newTheme);
      };

      mediaQuery.addEventListener("change", handleSystemThemeChange);

      return () => {
        mediaQuery.removeEventListener("change", handleSystemThemeChange);
      };
    }
  }, [mode, applyTheme]);

  const setMode = useCallback((nextMode: ColorMode) => {
    setPreferredMode(nextMode);
    persistMode(nextMode);
  }, []);

  const toggleTheme = useCallback(() => {
    setPreferredMode((currentMode) => {
      const nextMode: ColorMode = currentMode === "system" ? "light" : currentMode === "light" ? "dark" : "system";
      persistMode(nextMode);
      return nextMode;
    });
  }, []);

  if (preventDarkMode) {
    return (
      <ColorModeContext.Provider
        value={{
          mode: "light",
          colorMode: "light",
          isDarkTheme: false,
          setMode: () => {
            /* Default implementation */
          },
          toggleTheme: () => {
            /* Default implementation */
          },
        }}
      >
        {children}
      </ColorModeContext.Provider>
    );
  }

  return (
    <ColorModeContext.Provider value={{ mode, colorMode, isDarkTheme, setMode, toggleTheme }}>
      {children}
    </ColorModeContext.Provider>
  );
};
